import type { FleetBoardRun } from '@immo/api-contract';
import { costOf } from '@/components/fleet/pricing';

export type RunUsage = {
  /** Every step's total, nulls counted as 0. */
  readonly tokens: number;
  /** Euro, nulls counted as 0. */
  readonly cost: number;
  /** No step reported a total: the run's usage is not known, which is not the same as zero. */
  readonly unknown: boolean;
};

/** A run's tokens and euro across its ledger steps; `unknown` when it has none or none of them reported a total. */
export function runUsage(run: Pick<FleetBoardRun, 'steps'>): RunUsage {
  const unknown = run.steps.every((step) => step.totalTokens === null);
  const tokens = run.steps.reduce((sum, step) => sum + (step.totalTokens ?? 0), 0);
  const cost = run.steps.reduce(
    (sum, step) =>
      sum +
      costOf(step.model, {
        cacheRead: step.cacheReadInputTokens ?? 0,
        uncachedInput: step.uncachedInputTokens ?? 0,
        cacheWrite: step.cacheWriteInputTokens ?? 0,
        output: step.outputTokens ?? 0,
      }),
    0,
  );

  return { tokens, cost, unknown };
}

/** From the start (the claim when it never started) to the end, or to `now` while the run is live. */
export function runDurationMs(run: Pick<FleetBoardRun, 'claimedAt' | 'startedAt' | 'endedAt'>, now: number): number {
  const from = Date.parse(run.startedAt ?? run.claimedAt);
  const to = run.endedAt === null ? now : Date.parse(run.endedAt);

  return Math.max(0, to - from);
}

/** The first line of a run's end detail. */
export function firstLine(detail: string | null): string | null {
  const line = detail?.split('\n').find((part) => part.trim() !== '');

  return line === undefined ? null : line.trim();
}
